import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, ArrowLeft, Share2, Twitter, Facebook, Linkedin } from 'lucide-react';
import SEO from '@/components/SEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { sanitizeArticleHtml, getArticleIsolationStyles, getArticleScopeClass } from '@/utils/sanitizeArticleHtml';

interface PlatformBlogPost {
  id: string;
  title: string;
  slug: string;
  html_content: string;
  excerpt: string | null;
  meta_title: string | null;
  meta_description: string | null;
  featured_image_url: string | null;
  supporting_images: any[];
  word_count: number | null;
  reading_time: number | null;
  primary_keyword: string | null;
  secondary_keywords: string[] | null;
  published_at: string;
  created_at: string;
  updated_at?: string;
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<PlatformBlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState<PlatformBlogPost[]>([]);

  useEffect(() => {
    if (slug) {
      loadPost();
    }
  }, [slug]);

  const loadPost = async () => {
    try {
      setLoading(true);
      
      // Load the post with only necessary fields for faster initial load
      const { data: postData, error: postError } = await supabase
        .from('platform_blog_posts')
        .select('id, title, slug, html_content, excerpt, meta_title, meta_description, featured_image_url, supporting_images, word_count, reading_time, primary_keyword, secondary_keywords, published_at, created_at, updated_at')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (postError) throw postError;
      if (!postData) {
        navigate('/blog');
        return;
      }

      setPost(postData);

      // Load related posts in parallel (non-blocking, with minimal fields)
      supabase
        .from('platform_blog_posts')
        .select('id, title, slug, excerpt, featured_image_url, published_at')
        .eq('status', 'published')
        .neq('id', postData.id)
        .order('published_at', { ascending: false })
        .limit(3)
        .then(({ data: relatedData }) => {
          setRelatedPosts(relatedData || []);
        })
        .catch((err) => {
          console.error('Error loading related posts:', err);
        });
    } catch (error) {
      console.error('Error loading blog post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';
  const shareText = post?.excerpt || post?.meta_description || '';

  const handleShare = (platform: string) => {
    const url = encodeURIComponent(shareUrl);
    const title = encodeURIComponent(shareTitle);
    const text = encodeURIComponent(shareText);

    let shareLink = '';
    switch (platform) {
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
    }

    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8 md:py-16 max-w-4xl">
          {/* Skeleton Loader */}
          <div className="animate-pulse space-y-8">
            <div className="h-10 w-32 bg-muted rounded-lg"></div>
            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="h-6 w-20 bg-muted rounded-full"></div>
                <div className="h-6 w-24 bg-muted rounded-full"></div>
              </div>
              <div className="h-12 w-3/4 bg-muted rounded-lg"></div>
              <div className="h-6 w-full bg-muted rounded"></div>
              <div className="h-6 w-5/6 bg-muted rounded"></div>
            </div>
            <div className="aspect-video w-full bg-muted rounded-lg"></div>
            <div className="space-y-3">
              <div className="h-4 w-full bg-muted rounded"></div>
              <div className="h-4 w-full bg-muted rounded"></div>
              <div className="h-4 w-4/5 bg-muted rounded"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const canonicalUrl = `https://seoro.so/blog/${post.slug}`;
  const metaTitle = post.meta_title || post.title;
  const metaDescription = post.meta_description || post.excerpt || '';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO
        title={`${metaTitle} | Seoro Blog`}
        description={metaDescription}
        keywords={[post.primary_keyword, ...(post.secondary_keywords || [])].filter(Boolean).join(', ')}
        image={post.featured_image_url || undefined}
        type="article"
      />
      
      {/* Add structured data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: metaDescription,
            image: post.featured_image_url ? [post.featured_image_url] : [],
            datePublished: post.published_at || post.created_at,
            dateModified: post.updated_at || post.created_at,
            author: {
              '@type': 'Organization',
              name: 'Seoro',
              url: 'https://seoro.so'
            },
            publisher: {
              '@type': 'Organization',
              name: 'Seoro',
              logo: {
                '@type': 'ImageObject',
                url: 'https://seoro.so/favicon.svg'
              }
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': canonicalUrl
            }
          })
        }}
      />

      <Header />
      
      <main className="flex-1">
        <article className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-16 max-w-4xl animate-in fade-in duration-500">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="mb-4 sm:mb-8 -ml-2 sm:-ml-0"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>

          {/* Header */}
          <header className="mb-6 sm:mb-8">
            <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
              {post.primary_keyword && (
                <Badge variant="secondary" className="text-xs sm:text-sm">{post.primary_keyword}</Badge>
              )}
              {post.secondary_keywords?.slice(0, 3).map((keyword, idx) => (
                <Badge key={idx} variant="outline" className="text-xs sm:text-sm">{keyword}</Badge>
              ))}
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 sm:mb-4 leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm sm:text-base text-muted-foreground">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">{formatDate(post.published_at || post.created_at)}</span>
                </div>
                {post.reading_time && (
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="text-xs sm:text-sm">{post.reading_time} min read</span>
                  </div>
                )}
                {post.word_count && (
                  <span className="text-xs sm:text-sm">{post.word_count.toLocaleString()} words</span>
                )}
              </div>

              {/* Share Buttons */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('twitter')}
                  className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                >
                  <Twitter className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('facebook')}
                  className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                >
                  <Facebook className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare('linkedin')}
                  className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                >
                  <Linkedin className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
              </div>
            </div>

            {post.featured_image_url && (
              <div className="aspect-video w-full overflow-hidden rounded-lg sm:rounded-xl mb-6 sm:mb-10 bg-muted shadow-md sm:shadow-lg">
                <img
                  src={post.featured_image_url}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </header>

          {/* Content */}
          <style>{`
            .article-content-scope img {
              loading: lazy;
              decoding: async;
              content-visibility: auto;
            }
            .article-content-scope .image-container img {
              transition: opacity 0.3s ease-in-out;
            }
            .article-content-scope img[loading="lazy"] {
              content-visibility: auto;
            }
          `}</style>
          <div
            className={`prose prose-sm sm:prose-base md:prose-lg prose-slate max-w-none ${getArticleScopeClass()} 
              prose-headings:font-bold prose-headings:text-foreground
              prose-h1:text-2xl sm:prose-h1:text-3xl md:prose-h1:text-4xl prose-h1:mb-4 sm:prose-h1:mb-6 prose-h1:mt-6 sm:prose-h1:mt-8
              prose-h2:text-xl sm:prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mb-3 sm:prose-h2:mb-4 prose-h2:mt-6 sm:prose-h2:mt-8 prose-h2:border-b prose-h2:border-border prose-h2:pb-2
              prose-h3:text-lg sm:prose-h3:text-xl md:prose-h3:text-2xl prose-h3:mb-2 sm:prose-h3:mb-3 prose-h3:mt-4 sm:prose-h3:mt-6
              prose-h4:text-base sm:prose-h4:text-lg md:prose-h4:text-xl prose-h4:mb-2 prose-h4:mt-3 sm:prose-h4:mt-4
              prose-p:text-sm sm:prose-p:text-base prose-p:leading-6 sm:prose-p:leading-7 prose-p:mb-3 sm:prose-p:mb-4 prose-p:text-foreground/90
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-a:font-medium prose-a:break-words
              prose-strong:text-foreground prose-strong:font-semibold
              prose-ul:my-3 sm:prose-ul:my-4 prose-ul:pl-4 sm:prose-ul:pl-6 prose-li:my-1.5 sm:prose-li:my-2 prose-li:text-sm sm:prose-li:text-base prose-li:text-foreground/90
              prose-ol:my-3 sm:prose-ol:my-4 prose-ol:pl-4 sm:prose-ol:pl-6
              prose-blockquote:border-l-2 sm:prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-3 sm:prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:my-4 sm:prose-blockquote:my-6 prose-blockquote:text-sm sm:prose-blockquote:text-base prose-blockquote:text-foreground/80
              prose-code:text-xs sm:prose-code:text-sm prose-code:bg-muted prose-code:px-1 sm:prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-mono prose-code:break-words
              prose-pre:bg-muted prose-pre:rounded-lg prose-pre:p-3 sm:prose-pre:p-4 prose-pre:overflow-x-auto prose-pre:text-xs sm:prose-pre:text-sm
              prose-img:rounded-lg prose-img:shadow-md prose-img:my-4 sm:prose-img:my-8 prose-img:w-full prose-img:h-auto prose-img:-mx-4 sm:prose-img:mx-0
              prose-table:w-full prose-table:text-xs sm:prose-table:text-sm prose-table:my-4 sm:prose-table:my-6 prose-th:bg-muted prose-th:p-2 sm:prose-th:p-3 prose-th:font-semibold prose-td:p-2 sm:prose-td:p-3 prose-td:border-t prose-td:border-border prose-table:overflow-x-auto
              prose-hr:my-6 sm:prose-hr:my-8 prose-hr:border-border`}
            style={{
              ...getArticleIsolationStyles(),
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeArticleHtml(post.html_content, { 
                removeFirstH1: true, 
                removeFirstImage: true,
                featuredImageUrl: post.featured_image_url || undefined
              })
            }}
          />

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <section className="mt-8 sm:mt-12 md:mt-16 pt-6 sm:pt-8 border-t border-border">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-4 sm:mb-6">Related Posts</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                {relatedPosts.map((relatedPost) => (
                  <div
                    key={relatedPost.id}
                    className="cursor-pointer group"
                    onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                    onMouseEnter={() => {
                      // Prefetch related post data on hover
                      supabase
                        .from('platform_blog_posts')
                        .select('id')
                        .eq('slug', relatedPost.slug)
                        .eq('status', 'published')
                        .single()
                        .catch(() => {
                          // Silently fail - prefetch is optional
                        });
                    }}
                  >
                    {relatedPost.featured_image_url && (
                      <div className="aspect-video w-full overflow-hidden rounded-lg mb-3 bg-muted">
                        <img
                          src={relatedPost.featured_image_url}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <h3 className="font-bold text-foreground group-hover:text-primary transition-colors mb-2">
                      {relatedPost.title}
                    </h3>
                    {relatedPost.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;

